import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../api/api';
import type { Task, NewTask } from '../api/api';

interface TaskState {
  tasks: Task[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskState = {
  tasks: [],
  loading: false,
  error: null,
};

export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async () => {
    const response = await api.fetchTasks();
    return response;
  }
);

export const addTask = createAsyncThunk(
  'tasks/addTask',
  async (task: NewTask) => {
    const response = await api.addTask(task);
    return response;
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async (task: Task) => {
    const response = await api.updateTask(task);
    return response;
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (id: number) => {
    await api.deleteTask(id);
    return id;
  }
);

export const finishAndCloneTask = createAsyncThunk(
  'tasks/finishAndCloneTask',
  async (taskId: number, thunkAPI) => {
    const state: any = thunkAPI.getState();
    const tasks: Task[] = state.tasks.tasks;
    const task = tasks.find((t: Task) => t.id === taskId);
    if (!task) throw new Error('Task not found');

    const now = new Date();
    const localDateTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
      .toISOString()
      .slice(0, 19);

    // First, finish all other active tasks (those without endDate and not paused)
    const activeTasks = tasks.filter((t: Task) => 
      t.id !== taskId && 
      t.endDate === null && 
      t.isPaused !== 'T'
    );
    
    const completedTasks: Task[] = [];
    for (const activeTask of activeTasks) {
      const completed = await api.updateTask({
        ...activeTask,
        endDate: localDateTime,
        isPaused: null
      });
      completedTasks.push(completed);
    }

    // Finish the existing (paused) task - use its original endDate (when it was paused)
    const finished = await api.updateTask({
      ...task,
      endDate: task.endDate, // Keep the original pause time
      isPaused: null
    });

    // Create a clone that's active (new startDate, no endDate, not paused)
    const clone: NewTask = {
      name: task.name,
      category: task.category,
      subcategory: task.subcategory,
      startDate: localDateTime,
      endDate: null,
      isPaused: 'N'
    };

    const created = await api.addTask(clone);

    return { finished, created, completedTasks };
  }
);

const taskSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch tasks';
      })
      // Add task
      .addCase(addTask.fulfilled, (state, action) => {
        state.tasks.push(action.payload);
      })
      // Update task
      .addCase(updateTask.fulfilled, (state, action) => {
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      // Finish paused task and clone it as a new active task
      .addCase(finishAndCloneTask.fulfilled, (state, action) => {
        const { finished, created, completedTasks } = action.payload as { finished: Task; created: Task; completedTasks: Task[] };
        const idx = state.tasks.findIndex(t => t.id === finished.id);
        if (idx !== -1) {
          state.tasks[idx] = finished;
        }
        // Update all completed tasks in state
        completedTasks.forEach((completedTask: Task) => {
          const completedIdx = state.tasks.findIndex(t => t.id === completedTask.id);
          if (completedIdx !== -1) {
            state.tasks[completedIdx] = completedTask;
          }
        });
        // push newly created clone
        state.tasks.push(created);
      })
      // Delete task
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter(task => task.id !== action.payload);
      });
  },
});

export default taskSlice.reducer;
